import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import agent from "../api/agent";
import { adminPanelOptionToggle } from "../common/options/adminOptions";
import { AdminPanelOption, AdminUser, AdminUserReports, AdminUserProductLine, AdminUserRole, AdminUserTab, VerifyOrUpdatedUser, MailRequest, TempRegisterUser, TempRegisterUserActionForm } from "../models/admin";
import { ExistingPartner, Ta1AdminKobhoved, Ta1AdminKobhovedPartnerFormValue } from "../models/partners";

export default class AdminStore {
  adminAppUserRegistry = new Map<string, AdminUser>();
  adminAppUsersLoading = false;
  verifyOrUserUpdating = false;

  adminPanelOpts: AdminPanelOption[] = [];
  isAdminPanelOptsLoaded = false;

  adminKobhoveds: Ta1AdminKobhoved[] = [];
  adminKobhovedsLoading = false;
  selectedAdminKobhoved: Ta1AdminKobhoved | null = null;
  existingPartners: ExistingPartner[] = [];
  isSelectAll = false;
  isChecked = false;
  predicate = new Map();

  activeTab: number = 0;

  loadingTestMail = false;
  createPartnerLoading = false;
  createSelectPartnerLoading = false;

  tempRegisterUserRegistry = new Map<string, TempRegisterUser>();
  tempRegisterUserLoading = false;
  verifyTempRegisterUserLoading = false;


  constructor() {
    makeAutoObservable(this);
  }

  setActiveTab = (activeIndex: number) => {
    this.activeTab = activeIndex;
  };

  resetAdminStore = () => {
    this.adminAppUserRegistry.clear();
    this.adminPanelOpts = [];
    this.isAdminPanelOptsLoaded = false;
    this.setActiveTab(0);
    this.adminKobhoveds = [];
    this.selectedAdminKobhoved = null;
    this.tempRegisterUserRegistry.clear();
  }

  selectAllHandler = () => {
    if (this.adminKobhoveds.length > 0) {
      const filteredData = [...this.adminKobhoveds];

      filteredData.forEach((data) => {
        data.isChecked = !this.isSelectAll;
      })

      runInAction(() => {
        this.adminKobhoveds = filteredData;
        this.isSelectAll = !this.isSelectAll;

      })
    }
  }

  adminKobhovedCheckHandler = (id: string) => {
    if (id && this.adminKobhoveds.length > 0) {
      const filteredData = [...this.adminKobhoveds];
      let index = filteredData.findIndex(x => x.id === id);
      if (index >= 0) {
        let item = filteredData[index];
        item = {
          ...item,
          isChecked: !item.isChecked
        };
        filteredData[index] = item;
        const isSelectAll = filteredData.every(x => x.isChecked);
        runInAction(() => {
          this.adminKobhoveds = filteredData;
          this.isSelectAll = isSelectAll;
        })
      }
    }
  }

  setAdminKobhovedPartner = (predicate: string, value: string) => {
    const resetPredicate = (predicateKey: string) => {
      this.predicate.forEach((value, key) => {
        if (predicateKey === key) {
          this.predicate.delete(key);
        }
      })
    }
    switch (predicate) {
      case 'partnerName':
        resetPredicate('partnerName');
        this.predicate.set('partnerName', value.toUpperCase());
        break;
      case 'resellerName':
        resetPredicate('resellerName');
        this.predicate.set('resellerName', value.toUpperCase());
        break;
    }
  }

  get adminKobhovedPartnerParams() {
    const params = new URLSearchParams();
    this.predicate.forEach((value, key) => {
      params.append(key, value);
    })
    return params;
  }

  loadAdminKobhoveds = async () => {
    this.adminKobhovedsLoading = true;
    this.adminKobhoveds = [];
    try {
      const response = await agent.Admin.getKobhoveds(this.adminKobhovedPartnerParams);

      runInAction(() => {
        response.data.forEach((element: Ta1AdminKobhoved) => {
          element.isChecked = false;
        });

        this.adminKobhoveds = response.data;
        this.adminKobhovedsLoading = false;
        this.setAdminKobhovedPartner("partnerName", '');
        this.setAdminKobhovedPartner("resellerName", '');
      });

    } catch (error) {
      console.log(error);
      runInAction(() => (this.adminKobhovedsLoading = false));
    }
  };

  createPartner = async (formValues: Ta1AdminKobhovedPartnerFormValue) => {
    this.createPartnerLoading = true;
    try {
      await agent.Admin.addNewPartner(formValues);
      runInAction(() => {
        this.createPartnerLoading = false;
        toast.success("Data saved successfully");
      });
      this.loadAdminKobhoveds();

    } catch (error) {
      runInAction(() => (this.createPartnerLoading = false));
      //It is important to through error so we can display them under/in the form
      throw error;
    }
  }

  createExistingPartner = async (formValues: Ta1AdminKobhovedPartnerFormValue) => {
    this.createPartnerLoading = true;
    try {
      await agent.Admin.addExistingPartner(formValues);
      runInAction(() => {
        this.createPartnerLoading = false;
        toast.success("Data saved successfully");
      });
      this.loadAdminKobhoveds();
    } catch (error) {
      runInAction(() => (this.createPartnerLoading = false));
      //It is important to through error so we can display them under/in the form
      throw error;
    }
  }

  selectExistingPartner = async (userEmail: string) => {
    this.createSelectPartnerLoading = true;
    try {

      const response = await agent.Admin.getExistingPartner(userEmail);
      runInAction(() => {
        this.createSelectPartnerLoading = false;
        this.existingPartners = response;
      });
      // this.loadAdminKobhoveds();

    } catch (error) {
      runInAction(() => (this.createSelectPartnerLoading = false));
      //It is important to through error so we can display them under/in the form
      throw error;
    }
  }

  resetExistingPartner = () => {
    runInAction(() => {
      this.existingPartners = [];
    });
  }

  loadTempRegisterUser = async () => {
    this.tempRegisterUserLoading = true;
    this.tempRegisterUserRegistry.clear();
    try {
      const response = await agent.Admin.getTempRegisterUsers();
      runInAction(() => {
        response.forEach(user => {
          this.tempRegisterUserRegistry.set(user.email, user);
        })
        // this.adminAppUsers = response;
        this.tempRegisterUserLoading = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => (this.tempRegisterUserLoading = false));
    }
  };

  get adminTempUsers() {
    return Array.from(this.tempRegisterUserRegistry.values());
  }

  verifyTempRegsiterUser = async (uptUser: TempRegisterUserActionForm) => {
    this.verifyTempRegisterUserLoading = true;
    try {
      await agent.Admin.verifyTempRegisterUser(uptUser);
      runInAction(() => {

        let user = this.tempRegisterUserRegistry.get(uptUser.email);
        if (user) {
          const v = uptUser.actionName === "approved" ? true : false;
          user = {
            ...user,
            approved: v,
            blocked: !v,
            roles: [...uptUser.roles]
          }          
          this.tempRegisterUserRegistry.set(uptUser.email, user);
        }


        this.verifyTempRegisterUserLoading = false;
        toast.success("Data saved successfully");
      });
    } catch (error) {
      runInAction(() => {
        this.verifyTempRegisterUserLoading = false;
      });
      throw error;
    }
  }

  loadAdminAppUsers = async () => {
    this.adminAppUsersLoading = true;
    this.adminAppUserRegistry.clear();
    try {
      const response = await agent.Admin.getAdminUsers();
      runInAction(() => {
        response.forEach(user => {
          this.adminAppUserRegistry.set(user.email, user);
        })
        // this.adminAppUsers = response;
        this.adminAppUsersLoading = false;
      });
    } catch (error) {
      console.log(error);
      runInAction(() => (this.adminAppUsersLoading = false));
    }
  };

  get adminAppUsers() {
    return Array.from(this.adminAppUserRegistry.values());
  }

  setUserTabs = (email: string, tab: AdminUserTab) => {
    if (email && tab) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        const index = adminUser.tabs!.findIndex(x => x.tabName === tab.tabName);
        if (index >= 0) {
          adminUser.tabs[index].active = !tab.active;
          adminUser.isUpdated = true;
          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Selected tab is not valid')
    }
  }

  setUserProductionLines = (email: string, line: AdminUserProductLine) => {
    if (email && line) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        const index = adminUser.lines!.findIndex(x => x.line === line.line);
        if (index >= 0) {
          adminUser.lines[index].active = !line.active;
          adminUser.isUpdated = true;
          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Selected product line is not valid')
    }
  }

  setUserReport = (email: string, report: AdminUserReports) => {
    if (email && report) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        const index = adminUser.userReports!.findIndex(x => x.reportName === report.reportName);
        if (index >= 0) {
          adminUser.userReports[index].active = !report.active;
          adminUser.isUpdated = true;
          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Selected kam price is not valid')
    }
  }

  setUserRole = (email: string, role: AdminUserRole) => {
    if (email && role) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        if (adminUser.roles?.length > 0) {
          adminUser.isUpdated = true;
          adminUser.roles.forEach(userRole => {
            if (userRole.roleName === role.roleName) {
              userRole.active = !userRole.active;
            } else {
              userRole.active = false;
            }
          });

          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Selected role is not valid')
    }
  }

  setUserReportToggle = (email: string) => {
    if (email) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        if (adminUser.roles?.length > 0) {
          adminUser.isUpdated = true;
          adminUser.reporting = !adminUser.reporting;
          const userReports = adminUser.userReports;
          if (userReports && userReports.length > 0) {
            userReports.forEach((report) => {
              report.active = false;
            });
            adminUser.userReports = userReports;
          }
          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Problem to update User report')
    }
  }

  setUserPickUpToggle = (email: string) => {
    if (email) {
      const adminUser = this.adminAppUserRegistry.get(email);
      if (adminUser) {
        if (adminUser.roles?.length > 0) {
          adminUser.isUpdated = true;
          adminUser.pickUp = !adminUser.pickUp;
          runInAction(() => this.adminAppUserRegistry.set(email, adminUser));
        }
      }
    }
    else {
      toast.error('Problem to update User pick up')
    }
  }

  verifyOrUpdateUser = async (uptUser: VerifyOrUpdatedUser) => {
    this.verifyOrUserUpdating = true;
    try {
      await agent.Admin.verifyOrUpdateUser(uptUser);
      runInAction(() => {
        const adminUser = this.adminAppUserRegistry.get(uptUser.email);
        if (adminUser) {
          adminUser.role = uptUser.role;
          adminUser.isUpdated = false;
          if (!adminUser.approved && !adminUser.emailConfirmed) {
            adminUser.activity = 'Email Conf.'
          }
          this.adminAppUserRegistry.set(uptUser.email, adminUser);
        }
        this.verifyOrUserUpdating = false;
        toast.success("Data saved successfully");
      });
    } catch (error) {
      runInAction(() => {
        this.verifyOrUserUpdating = false;
      });
      throw error;
    }
  }

  loadAdminPanelOptions = () => {
    const opts = adminPanelOptionToggle();
    this.adminPanelOpts = opts;
    this.isAdminPanelOptsLoaded = true;
  }

  setAdminPanelOptions = (optKey: string) => {
    const newArr = [...this.adminPanelOpts];
    const previousSelectedIndex = newArr.findIndex(x => x.toggle);
    const selectedIndex = newArr.findIndex(x => x.key === optKey);
    if (selectedIndex >= 0) {
      newArr[selectedIndex].toggle = true;
    }
    if (previousSelectedIndex >= 0 && previousSelectedIndex !== selectedIndex) {
      newArr[previousSelectedIndex].toggle = false;
    }
    runInAction(() => this.adminPanelOpts = newArr);
  }

  testMail = async (opt: number) => {
    this.loadingTestMail = true;
    try {
      const mail: MailRequest = {
        toEmail: 'rsb@tier1asset.com',
        body: 'test mail',
        subject: 'Test partner mail',
        opt: opt
      };
      await agent.Admin.testEmail(mail);
      runInAction(() => {
        this.loadingTestMail = false;
        toast.success("Mail sent successfully");
      });
    } catch (error) {
      runInAction(() => {
        this.loadingTestMail = false;
      });
      throw error;
    }
  }



}
